import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { MovingBorderWrapper } from "@/components/ui/moving-border";
import { SEO, seoConfig } from "@/hooks/useSEO";

// Images
import heroImage from "@/assets/usecases/agences/hero.webp";
import feature1Image from "@/assets/usecases/agences/feature-1.webp";
import feature2Image from "@/assets/usecases/agences/feature-2.webp";
import feature3Image from "@/assets/usecases/agences/feature-3.webp";

const features = [
  {
    number: "1",
    title: "Le canal que vos clients possèdent vraiment",
    text: "Proposez à vos clients de construire leur propre liste d'abonnés via Unotif. Contrairement aux réseaux sociaux ou aux emails, ils contrôlent tout. Pas d'algorithme, pas de filtre spam, livraison garantie sur écran verrouillé. Vendez ça comme l'assurance communication de leur business. Vos clients comprennent immédiatement la valeur.",
    image: feature1Image,
    imagePosition: "left" as const,
  },
  {
    number: "2",
    title: "Des métriques qui prouvent votre valeur",
    text: "Montrez à vos clients des chiffres concrets : taux de visibilité proche de 100%, engagement 5 à 10 fois supérieur aux emails. Avec Unotif, vous ne vendez plus de l'espoir, vous vendez des résultats mesurables. Parfait pour justifier vos honoraires et prouver votre impact.",
    image: feature2Image,
    imagePosition: "right" as const,
  },
  {
    number: "3",
    title: "Un service récurrent qui vous rapporte",
    text: "Packagez Unotif comme service mensuel : création et gestion des cartes, stratégie de notifications, suivi des performances. Déployez en moins d'une heure pour vos clients, facturez mensuellement. Revenus récurrents + clients fidélisés = la combinaison parfaite pour une agence rentable.",
    image: feature3Image,
    imagePosition: "left" as const,
  },
];

const badges = [
  "Nouveau service premium",
  "Revenus mensuels garantis",
  "Clients qui restent",
];

export default function UseCasesAgences() {
  return (
    <div className="min-h-screen flex flex-col">
      <SEO {...seoConfig.useCasesAgences} />
      <Header />

      {/* SECTION 1 - HERO */}
      <section className="relative pt-24 lg:pt-40 pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px] overflow-hidden">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 xl:gap-16 items-center">
            {/* Left: Text content */}
            <div className="max-w-2xl text-center lg:text-left mx-auto lg:mx-0">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-foreground mb-6">
                Devenez l'agence qui apporte un vrai <span className="text-[#0c97a9]">canal propriétaire à ses clients</span>
              </h1>

              <p className="leading-relaxed mb-8 font-medium text-[#4f5b5d] text-base sm:text-lg">
                Vos clients veulent des résultats mesurables et un ROI clair. <span className="font-semibold text-[#0c97a9]">Unotif</span> vous permet de leur vendre un canal de communication direct avec des métriques d'engagement exceptionnelles. Un service à forte marge qui vous positionne comme partenaire stratégique indispensable.
              </p>

              <div className="flex flex-col items-center lg:items-start">
                <MovingBorderWrapper duration={3000} borderRadius="0.5rem">
                  <Button asChild size="lg" className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-6">
                    <a href="https://app.unotif.com/auth?mode=signup">
                      Créez votre première carte
                    </a>
                  </Button>
                </MovingBorderWrapper>
                <p className="text-sm text-muted-foreground mt-2">
                  👉 Sans engagement, sans carte bancaire
                </p>
              </div>
            </div>

            {/* Right: Hero Image */}
            <div className="hidden lg:flex justify-center lg:justify-end items-center">
              <img
                src={heroImage}
                alt="Agences marketing avec Unotif"
                loading="lazy"
                className="rounded-2xl max-w-full h-auto"
                style={{
                  boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                }}
              />
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 2 - Un service qui se vend tout seul */}
      <section className="bg-[#FAFBFB] pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-[37.5px] sm:pb-[47.5px] lg:pb-[65px]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-6">
              Un service qui <span className="text-[#0c97a9]">se vend tout seul</span>
            </h2>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d]">
              <span className="font-semibold text-[#0c97a9]">Unotif</span> ne concurrence pas vos services actuels. Il les complète en apportant ce qui manque : un canal fiable et mesurable. La clé : montrer à vos clients la différence entre espérer être vu et garantir d'être vu.
            </p>
          </div>

          {/* Features */}
          <div className="space-y-12 sm:space-y-16 lg:space-y-20">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  feature.imagePosition === "right" ? "lg:grid-flow-dense" : ""
                }`}
              >
                {/* Image */}
                <div className={feature.imagePosition === "right" ? "lg:col-start-2" : ""}>
                  <img
                    src={feature.image}
                    alt={feature.title}
                    loading="lazy"
                    className="rounded-2xl w-full h-auto"
                    style={{
                      boxShadow: "0 8px 32px -8px rgba(0,0,0,0.12), 0 4px 16px -4px rgba(0,0,0,0.08)",
                    }}
                  />
                </div>

                {/* Text */}
                <div className={feature.imagePosition === "right" ? "lg:col-start-1 lg:row-start-1" : ""}>
                  <div className="flex items-start gap-4">
                    <span className="text-4xl sm:text-5xl font-bold text-[#0c97a9]">
                      {feature.number}.
                    </span>
                    <div>
                      <h3 className="text-xl sm:text-2xl font-bold text-[#022226] mb-4">
                        {feature.title}
                      </h3>
                      <p className="text-[14px] sm:text-base font-medium leading-relaxed text-[#495456]">
                        {feature.text}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Result text */}
          <div className="text-center mt-12 sm:mt-16 lg:mt-20 max-w-3xl mx-auto">
            <p className="text-base sm:text-lg font-semibold leading-relaxed text-[#074155]">
              Résultat : canal marketing privé pour vos clients, revenus récurrents pour vous.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 - Positionnez-vous comme l'agence incontournable */}
      <section className="pt-[37.5px] sm:pt-[47.5px] lg:pt-[65px] pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-[#074155] mb-6">
              Positionnez-vous comme <span className="text-[#0c97a9]">l'agence incontournable.</span>
            </h2>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d] mb-4">
              Vous avez le choix. Continuer à vendre les mêmes services que tout le monde, vous battre sur les prix et perdre des clients.
            </p>
            <p className="text-base sm:text-lg font-medium leading-relaxed text-[#4f5b5d] mb-10">
              Ou proposer un canal innovant que vos concurrents n'ont pas, avec des résultats mesurables qui fidélisent vos clients et génèrent des revenus récurrents.
            </p>

            {/* Badges */}
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-6">
              {badges.map((badge, index) => (
                <span
                  key={index}
                  className="rounded-full border border-[#074155] bg-white px-4 py-2 text-sm sm:text-base font-medium text-[#074155]"
                >
                  {badge}
                </span>
              ))}
            </div>

            {/* Callout */}
            <p className="text-base sm:text-lg font-medium text-[#074155]">
              👀 Démarquez-vous vraiment.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 4 - CTA Final */}
      <section className="pb-16 sm:pb-20 lg:pb-24">
        <div className="container mx-auto px-6 lg:px-12 xl:px-20">
          <Card className="max-w-2xl mx-auto text-center p-8 sm:p-10 lg:p-12 border border-gray-200 shadow-lg">
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-[#074155] mb-4">
              Testez Unotif avec votre premier client !
            </h2>
            <p className="text-base sm:text-lg font-medium text-[#4f5b5d] mb-8">
              Créez gratuitement votre première carte et découvrez comment transformer les notifications push en service récurrent pour votre agence.
            </p>
            <div className="flex flex-col items-center">
              <Button asChild size="lg" className="bg-primary hover:bg-[hsl(187,87%,32%)] text-primary-foreground shadow-lg hover:shadow-xl transition-all duration-300 text-base px-8 py-6">
                <a href="https://app.unotif.com/auth?mode=signup">
                  Commencer maintenant
                </a>
              </Button>
              <p className="text-sm text-muted-foreground mt-3">
                Sans engagement
              </p>
            </div>
          </Card>
        </div>
      </section>

      <Footer />
    </div>
  );
}
